import { Component } from "@angular/core";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import {
  AlertController,
  IonicPage,
  LoadingController,
  NavController,
  NavParams,
} from "ionic-angular";
import { Subscription } from "rxjs";
import { AccessControlService } from "../../../app/access-control/access-control-service";
import { AuthService } from "../../../app/auth/auth.service";
import {
  toDto,
  toFormValue,
} from "../../../models/company-details/company-location-mapper";
import { CompanyManagementService } from "../../../providers/company-management-service/company-management-service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { UsersService } from "../../../providers/users-service/users.service";
import { CompanyPage } from "../company";

@IonicPage({
  segment: "company/company-management/facility",
})
@Component({
  selector: "facility",
  templateUrl: "facility.html",
})
export class Facility extends CompanyPage {
  parentForm: FormGroup;
  facilityData: any;
  companyName: string = "";
  companyId: string = "";
  locationGroupName: string = "";
  _isEditMode: boolean = false;
  subscriptions: Subscription[] = [];

  get isEditMode() {
    return this._isEditMode;
  }

  set isEditMode(value: boolean) {
    this._isEditMode = value;
  }

  constructor(
    protected accessControlService: AccessControlService,
    protected navCtrl: NavController,
    protected navParams: NavParams,
    protected loader: LoadingController,
    protected userService: UsersService,
    protected stateManagementService: StateManagementService,
    protected formBuilder: FormBuilder,
    protected companyManagementService: CompanyManagementService,
    protected alertController: AlertController,
    protected authSvc: AuthService
  ) {
    super(
      accessControlService,
      navCtrl,
      navParams,
      userService,
      stateManagementService,
      authSvc
    );
    this.parentForm = this.formBuilder.group({
      facility: [],
    });

    this.facilityData = this.navParams.get("facilityData");
    this.companyName = this.navParams.get("companyName");
    this.companyId = this.navParams.get("companyId");
    this.locationGroupName = this.navParams.get("locationGroupName");
    if (this.facilityData) {
      this.isEditMode = true;
      // The stored record is NESTED (address{}, contact{phones[]}, hospiceInfo{}); the
      // form is FLAT. Patching the raw record in left every field blank.
      this.parentForm.patchValue({
        facility: toFormValue(this.facilityData),
      });
    }
  }

  navigateBack() {
    this.navCtrl.pop();
  }

  toggleEditMode() {
    this.isEditMode = !this.isEditMode;
  }

  saveData() {
    const locationForm = this.parentForm.get("facility") as FormControl;

    if (!locationForm || !locationForm.valid) {
      this.presentAlert(
        "Invalid Submission",
        "Missing required fields. Please review."
      );
      return;
    }

    if (!this.companyId) {
      // The companies API keys the write on /companies/{companyId}/locations — without
      // it we would POST to a nonsense URL and lose the user's work silently, which is
      // the very failure this ticket is fixing. Say so instead.
      this.presentAlert(
        "Unable to Save",
        "No company is selected for this facility. Please reopen it from the Company Locations screen."
      );
      return;
    }

    const payload = this.mapObjectFromForm();
    const spinner = this.loader.create({ content: "Saving..." });
    spinner.present();

    const save$ = this.facilityData
      ? this.companyManagementService.updateLocation(this.companyId, payload)
      : this.companyManagementService.createLocation(this.companyId, payload);

    this.subscriptions.push(
      save$.subscribe(
        (saved) => {
          spinner.dismiss();
          // Only leave the form once the write actually succeeded — the old code
          // navigated back unconditionally, so a user could never tell a failed save
          // from a successful one.
          this.navigateBack();
        },
        (err) => {
          spinner.dismiss();
          this.presentAlert(
            "Unable to Save",
            "The facility could not be saved. Please try again."
          );
        }
      )
    );
  }

  /** The flat form value mapped into the nested CompanyLocationDto the API expects.
   *  On edit the record being edited is passed through so the PUT (which overwrites the
   *  stored item wholesale) cannot blank the fields the form does not show. */
  mapObjectFromForm(): any {
    return toDto(
      this.parentForm.get("facility").value,
      this.companyId,
      this.facilityData
    );
  }

  private presentAlert(title: string, message: string) {
    this.alertController
      .create({
        title,
        message,
        buttons: [{ text: "Ok", handler: () => {} }],
      })
      .present();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    super.ngOnDestroy();
  }
}



// WEBPACK FOOTER //
// ./src/pages/company/facility/facility.ts